import type { ElementType } from 'react';
import type React from 'react';
import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import type { ElementSize } from '../../@types/types';
import sharedClasses from '../utils/shared-classes';

import Icon from './Icon';
import LoadingOverlay from './LoadingOverlay';
import type { SvgIconName } from './Svg';

type ButtonKindColor = 'green' | 'primary' | 'secondary' | 'tertiary' | 'danger-secondary' | 'danger' | 'ghost';

const buttonClasses = cva(
  'inline-flex overflow-clip items-center justify-center relative transition duration-75 ease-in font-medium text-sm rounded-md shadow-xs disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 gap-2.5',
  {
    variants: {
      kind: {
        green:
          'text-white bg-success-600 enabled:hover:bg-success-800 enabled:active:bg-success-900 disabled:bg-success-300',
        primary:
          'text-white bg-primary-600 enabled:hover:bg-primary-800 enabled:active:bg-primary-900 disabled:bg-primary-300',
        secondary:
          'enabled:active:bg-info-200 bg-white border border-info-300 disabled:text-info-600 shadow-xs enabled:hover:bg-info-100 text-info-700 font-normal',
        tertiary:
          'enabled:active:bg-info-200 bg-white border border-info-500 disabled:text-info-600 enabled:hover:bg-info-100 text-primary-800',
        'danger-secondary':
          'enabled:active:bg-info-200 bg-white border border-info-500 disabled:text-info-600 enabled:hover:bg-info-100 text-error-800',
        danger: 'text-white bg-error-600 enabled:hover:bg-error-500 enabled:active:bg-error-700 disabled:bg-error-500',
        ghost: 'text-info-700 bg-white font-normal disabled:bg-white/30 enabled:hover:bg-info-100 shadow-lg',
      },
    },
  },
);

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  as?: ElementType;
  iconName?: SvgIconName;
  isDisabled?: boolean;
  kind?: ButtonKindColor;
  size?: ElementSize;
  type?: string;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    as: Component = 'button',
    children,
    className,
    iconName,
    isDisabled,
    kind = 'secondary',
    type = 'button',
    loading,
    ...props
  },
  ref,
) {
  return (
    <Component
      className={twMerge(
        clsx(
          buttonClasses({ kind }),
          !clsx(className).match(/(p[xy]?)-(\d+)/) && 'px-4',
          !clsx(className).match(/(h)-(\d+)/) && 'h-12',
          (loading || isDisabled) && sharedClasses.disabled,
          className,
        ),
      )}
      disabled={isDisabled || loading}
      ref={ref}
      type={type}
      {...props}
    >
      {iconName && !loading && <Icon name={iconName} className="mr-2 w-4" />}
      <LoadingOverlay active={loading} size="xs" transparent />
      {children}
    </Component>
  );
});

export default Button;
